<script setup lang="ts">
import { debounce } from "lodash-es";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { removeProgress } from "@/services/middlewareService/learnerProgress";
import { add } from "@/services/profileService/myList";
import { updateUserActions } from "@/services/profileService/userActions";
import { urlContentTypes } from "@/utils/contentRemovable";
import isUnauth from "@/utils/isUnauth";
import telemetry from "@/utils/telemetry";

interface Props {
  result: any;
  index?: number;
  status?: any;
  forCarousel?: boolean;
  cardView?: string;
  cardStyle?: string;
  priceView?: string;
  isRemovable?: boolean;
  topicView?: boolean;
  isAdminContent?: boolean;
  isStoreContent?: boolean;
  showDescription?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  index: 0,
  status: null,
  forCarousel: false,
  cardView: "Grid",
  cardStyle: "Grid",
  priceView: "USD",
  isRemovable: false,
  topicView: false,
  isAdminContent: false,
  isStoreContent: false,
  showDescription: true,
});

const store = useStore();
const router = useRouter();
const route = useRoute();

const hideRecommendation = ref(false);

const inMyList = computed(() => store.getters["user/inMyList"]);
const freeId = computed(() => store.getters["content/freeId"]);

const freeContent = computed((): boolean => {
  return props.result?.content_bundle?.includes(freeId.value);
});

const resultLink = computed(() => {
  let tutorialId;
  if (props.result?.friendly_url?.length > 0) {
    tutorialId = props.result.friendly_url;
  } else {
    tutorialId = props.result.id;
  }

  if (!tutorialId) {
    return "";
  }
  return router.resolve({
    name: "Tutorial",
    params: { tutorialId },
  }).href;
});

const removeFromMyProgress = (content: any) => {
  store.commit("user/removeFromMyProgress", content);
};

const fetchListByType = (type: any) => {
  return store.dispatch("user/fetchListByType", type);
};

const removeContentFromMyList = (payload: any) => {
  return store.dispatch("user/removeContentFromMyList", payload);
};

const fetchProgress = () => {
  return store.dispatch("user/fetchProgress");
};

const addContentToMyList = () => {
  const param_type = route.name != "forYou" ? props.result.type : null;
  add({
    type: param_type,
    content_id: props.result.id ? parseInt(props.result.id) : null,
    content_guid: props.result.guid != "" ? props.result.guid : null,
    deleted_at: null,
  })
    ?.then(() => {
      fetchListByType(param_type);
    })
    .catch(() => {
      // do nothing
    });

  telemetry.add_to_list(
    "add-contentType-to-list",
    route.fullPath,
    "tutorial",
    props.result.title,
    props.result.id
  );
};

const bookmarkMe = (content: any) => {
  inMyList.value(content)
    ? removeContentFromMyList({
        routeName: route.name,
        result: content,
      })
    : addContentToMyList();
};

const debouncedBookmarkMe = debounce(
  (content: any) => bookmarkMe(content),
  500
);

const sendTelemetry = async () => {
  let telemetryResponse = telemetry.external(
    {
      action: "launch-contentType",
      url: window.location.origin + resultLink.value,
      title: props.result.title,
      id: props.result.id,
      page: route.fullPath,
      type: props.result.type,
      relevance: props.result.relevance,
      sortOrder: props.result?.assigned_content
        ? props.result.assigned_content?.index
        : props.index,
      assignedContent: props.result?.assigned_content
        ? props.result.assigned_content?.isAssigned
        : undefined,
    },
    isUnauth()
  );
  telemetryResponse
    .then(() => {
      if (resultLink.value.startsWith("https://")) {
        window.open(resultLink.value, "_self", "noopener");
      } else {
        router.push(resultLink.value);
      }
    })
    .catch(() => {
      // do nothing
    });
};

const hideCTA = (guid: any) => {
  hideRecommendation.value = true;
  updateUserActions({
    guid: guid,
    element: "NoThanks",
    action: "Close",
  });
};

const sendRmTelemetry = async (content: any) => {
  const contentId = content.content_id || content.id || "";
  return await telemetry.remove_from_myLearning(
    window.location.origin + route.fullPath,
    `/${urlContentTypes(content.type)}/${contentId}`,
    content.title || content.name || "",
    content.type,
    contentId,
    isUnauth()
  );
};

const removeFromMyProgressList = async (content: any) => {
  removeProgress(content.guid, content.type)
    .then(() => {
      sendRmTelemetry(content);
      removeFromMyProgress(content);
      fetchProgress();
    })
    .catch(() => {
      //do nothing
    });
};

defineExpose({
  sendTelemetry,
  freeContent,
  addContentToMyList,
  removeContentFromMyList,
  removeFromMyProgressList,
  hideCTA,
  bookmarkMe,
  debouncedBookmarkMe,
  resultLink,
});
</script>

<template>
  <div
    v-if="result && !hideRecommendation"
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
  >
    <div data-cy="tutorial-card" class="relative h-full">
      <the-card
        v-if="!isStoreContent"
        :is-unauth="isUnauth()"
        :for-carousel="forCarousel"
        :card-view="props.cardView"
        :card-style="props.cardStyle"
        :result="result"
        :status="status"
        :cy="'tutorial-card-link'"
        :is-removable="isRemovable"
        :link="resultLink"
        :show-description="props.showDescription"
        @navigate="sendTelemetry"
        @bookmark="debouncedBookmarkMe(result)"
        @remove-progress="removeFromMyProgressList(result)"
        :is-admin-content="isAdminContent"
      />
      <the-product-card
        v-else
        :is-unauth="isUnauth()"
        :card-view="props.cardView"
        :card-style="props.cardStyle"
        :price-view="priceView"
        :result="result"
        :status="status"
        :link="resultLink"
        @navigate="sendTelemetry"
        :cy="'tutorial-product-card'"
        :show-description="props.showDescription"
        @bookmark="debouncedBookmarkMe(result)"
        :is-admin-content="isAdminContent"
      />
    </div>
  </div>
</template>
